import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Expand, X } from "lucide-react";
import { getCategoryBySlug } from "../data/categories";
import BookingForm from "./BookingForm";
import Footer from "./Footer";
import NotFound from "./NotFound";
import "./ProductCategoryPage.css";

export default function ProductCategoryPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const category = getCategoryBySlug(slug);

  const [activeIndex, setActiveIndex] = useState(null);
  const [openBooking, setOpenBooking] = useState(false);
  const [startX, setStartX] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (category) document.title = `${category.title} — Demollari E.E.E`;
  }, [category]);

  useEffect(() => {
    if (activeIndex === null) return;
    const handleKey = (e) => {
      if (e.key === "Escape") setActiveIndex(null);
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  if (!category) return <NotFound />;

  const images = category.images;

  const goNext = () => setActiveIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  const goPrev = () => setActiveIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));

  const handleDragStart = (clientX) => setStartX(clientX);
  const handleDragEnd = (clientX) => {
    if (startX === null) return;
    const diff = startX - clientX;
    if (diff > 50) goNext();
    if (diff < -50) goPrev();
    setStartX(null);
  };

  return (
    <div className="category-page">
      <button
        type="button"
        className={`btn back-btn ${openBooking || activeIndex !== null ? "hidden-btn" : ""}`}
        onClick={() => navigate(-1)}
        aria-label="Go back"
      >
        <ArrowLeft size={18} />
        <span>Back</span>
      </button>

      <div className="category-content">
        <div className="category-top">
          <div className="category-gallery">
            {images.map((image, idx) => (
              <button
                type="button"
                className="gallery-card"
                key={image.src}
                onClick={() => setActiveIndex(idx)}
              >
                <img src={image.src} alt={image.alt} loading="lazy" />

                {image.name ? (
                  <div className="gallery-overlay gallery-overlay-label">
                    <h3>{image.name}</h3>
                    {image.description && <p>{image.description}</p>}
                  </div>
                ) : (
                  <div className="gallery-overlay gallery-overlay-icon">
                    <Expand size={22} />
                  </div>
                )}
              </button>
            ))}
          </div>

          <aside className="category-info">
            <h1>{category.title}</h1>

            {category.description.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}

            {category.features && (
              <>
                <h3>Why choose us?</h3>
                <ul>
                  {category.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>
              </>
            )}

            <button type="button" className="btn book-btn" onClick={() => setOpenBooking(true)}>
              Order Now
            </button>
          </aside>
        </div>
      </div>

      <Footer />

      {activeIndex !== null && (
        <div
          className="lightbox"
          onClick={() => setActiveIndex(null)}
          onMouseDown={(e) => handleDragStart(e.clientX)}
          onMouseUp={(e) => handleDragEnd(e.clientX)}
          onTouchStart={(e) => handleDragStart(e.touches[0].clientX)}
          onTouchEnd={(e) => handleDragEnd(e.changedTouches[0].clientX)}
        >
          <button
            type="button"
            className="close-btn"
            onClick={(e) => {
              e.stopPropagation();
              setActiveIndex(null);
            }}
            aria-label="Close"
          >
            <X size={26} />
          </button>

          <button
            type="button"
            className="nav-btn left"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            aria-label="Previous image"
          >
            <ChevronLeft size={28} />
          </button>

          <img
            src={images[activeIndex].src}
            alt={images[activeIndex].alt}
            onClick={(e) => e.stopPropagation()}
            draggable={false}
          />

          <button
            type="button"
            className="nav-btn right"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            aria-label="Next image"
          >
            <ChevronRight size={28} />
          </button>
        </div>
      )}

      <BookingForm
        isOpen={openBooking}
        onClose={() => setOpenBooking(false)}
        initialProduct={category.orderLabel}
      />
    </div>
  );
}
